<template>
    <div class="comp-item-create-form-component">
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>

        <b-form 
            v-if="formReady"
            key="formReady"
            @submit.prevent="handleSubmit"
        >
            <b-form-group
                v-if="isLocalFieldNameEnabled('comp_item_type_id')"
                :id="`${inputPrefix}-group-comp-item-type-id`"
                description="The category of this Document Type"
                label="Document Category"
                :label-for="`${inputPrefix}-input-comp-item-type`"
                label-cols="4"
                label-class="font-weight-bold"
                required
                invalid-feedback="Please select a Document Category"
                :state="fieldTouchedStatus['comp_item_type_id'] && compItemTypeErrors.length !== 0 ? false : null"
                class="mt-3"
            >
                    <b-form-select 
                        :id="`${inputPrefix}-input-comp-item-type-id`" 
                        v-model="formData.comp_item_type_id" 
                        :state="fieldTouchedStatus['comp_item_type_id'] && compItemTypeErrors.length !== 0 ? false : null" 
                        :options="itemTypeOptions"
                        value-field="value"
                        text-field="label"
                        @change="setFieldTouched('comp_item_type_id')"
                        ></b-form-select>
                    <template #invalid-feedback>
                        <div v-for="err in compItemTypeErrors" :key="err" class="mt-1 text-danger font-size-70">{{ err }}</div>
                    </template>
            </b-form-group>

            <b-form-group
                v-if="isLocalFieldNameEnabled('kind_id')"
                :id="`${inputPrefix}-group-kind-id`"
                description="The Kind of this Document Type - what format is it?"
                label="Kind of Document"
                :label-for="`${inputPrefix}-input-kind-id`"
                label-cols="4"
                label-class="font-weight-bold"
                required
                invalid-feedback="Please select a Kind of Document"
                :state="fieldTouchedStatus['kind_id'] && kindErrors.length !== 0 ? false : null"
                class="mt-3"
            >
                    <b-form-select 
                        :id="`${inputPrefix}-input-kind-id`" 
                        v-model="formData.kind_id" 
                        :state="fieldTouchedStatus['kind_id'] && kindErrors.length !== 0 ? false : null" 
                        :options="kindOptions"
                        value-field="value"
                        text-field="label"
                        @change="setFieldTouched('kind_id')"
                        ></b-form-select>
                    <template #invalid-feedback>
                        <div v-for="err in kindErrors" :key="err" class="mt-1 text-danger font-size-70">{{ err }}</div>
                    </template>
            </b-form-group>

            <b-form-group
                v-if="isLocalFieldNameEnabled('title')"
                :id="`${inputPrefix}-group-title`"
                description="A descriptive name, visible to suppliers and internal users"
                label="Name"
                :label-for="`${inputPrefix}-input-title`"
                label-cols="4"
                label-class="font-weight-bold"
                required
                invalid-feedback="Please enter a Name for this Document Type"
                :state="fieldTouchedStatus['title'] && titleErrors.length !== 0 ? false : null" 
                class="mt-4"
            >
                    <b-form-input 
                        :id="`${inputPrefix}-input-title`" 
                        v-model="formData.title" 
                        :state="fieldTouchedStatus['title'] && titleErrors.length !== 0 ? false : null" 
                        trim
                        @blur="setFieldTouched('title')"
                    ></b-form-input>
                    <template #invalid-feedback>
                        <div v-for="err in titleErrors" :key="err" class="mt-1 text-danger font-size-70">{{ err }}</div>
                    </template>
            </b-form-group>

            <b-form-group
                v-if="isLocalFieldNameEnabled('singleOrMultiple')"
                :id="`${inputPrefix}-group-singleOrMultiple`"
                description="Use 'Single' if only one document of this type is allowed per vendor.  Use 'Multiple' if multiple documents of this type are allowed per vendor."
                label="Single or Multiple per Vendor?"
                :label-for="`${inputPrefix}-input-singleOrMultiple`"
                label-cols="4"
                label-class="font-weight-bold"
                required
                invalid-feedback="Please select an option"
                :state="fieldTouchedStatus['singleOrMultiple'] && singleOrMultipleErrors.length !== 0 ? false : null" 
                class="mt-4"
            >
                    <b-form-radio-group 
                        :id="`${inputPrefix}-input-singleOrMultiple`" 
                        v-model="formData.singleOrMultiple" 
                        :state="fieldTouchedStatus['singleOrMultiple'] && singleOrMultipleErrors.length !== 0 ? false : null" 
                        value-field="value"
                        text-field="label"
                        class="mb-3"
                        @change="setFieldTouched('singleOrMultiple')"
                    >
                        <b-form-radio value="single"><CompSingleMultipleChip value="single" /></b-form-radio>
                        <b-form-radio value="multiple"><CompSingleMultipleChip value="multiple" /></b-form-radio>
                    </b-form-radio-group>
                    <template #invalid-feedback>
                        <div v-for="err in singleOrMultipleErrors" :key="err" class="mt-1 text-danger font-size-70">{{ err }}</div>
                    </template>
            </b-form-group>

            <b-form-group
                v-if="isLocalFieldNameEnabled('enable_valid_dates')"
                :id="`${inputPrefix}-group-enable_valid_dates`"
                description="Select 'Yes' if documents of this type have specific valid date ranges, or otherwise can expire."
                label="Enable expiration tracking?"
                :label-for="`${inputPrefix}-input-enable_valid_dates`"
                label-cols="4"
                label-class="font-weight-bold"
                required
                invalid-feedback="Please select an option"
                :state="fieldTouchedStatus['enable_valid_dates'] && enableValidDatesErrors.length !== 0 ? false : null" 
                class="mt-4"
            >
                    <b-form-radio-group 
                        :id="`${inputPrefix}-input-enable_valid_dates`" 
                        v-model="formData.enable_valid_dates" 
                        :state="fieldTouchedStatus['enable_valid_dates'] && enableValidDatesErrors.length !== 0 ? false : null" 
                        value-field="value"
                        text-field="label"
                        class="mb-3"
                        @change="setFieldTouched('enable_valid_dates')"
                    >
                        <b-form-radio :value="true" class="max-width-150">
                            <span class="font-weight-bold">Yes</span>&nbsp;
                            <span class="text-muted">track expiration dates</span>
                        </b-form-radio>
                        <b-form-radio :value="false" class="max-width-150">
                            <span class="font-weight-bold">No</span>&nbsp;
                            <span class="text-muted">disable expiration tracking</span>
                        </b-form-radio>
                    </b-form-radio-group>
                    <template #invalid-feedback>
                        <div v-for="err in enableValidDatesErrors" :key="err" class="mt-1 text-danger font-size-70">{{ err }}</div>
                    </template>
            </b-form-group>

            <b-form-group
                v-if="isLocalFieldNameEnabled('notes')"
                :id="`${inputPrefix}-group-notes`"
                description="Notes about this item, visible only to internal users"
                label="Notes"
                :label-for="`${inputPrefix}-input-notes`"
                label-cols="4"
                label-class="font-weight-bold"
                required
                invalid-feedback="Please input notes"
                :state="fieldTouchedStatus['notes'] && notesErrors.length !== 0 ? false : null" 
                class="mt-4"
            >
                    <b-form-textarea 
                        :id="`${inputPrefix}-input-notes`" 
                        v-model="formData.notes" 
                        rows="8"
                        :state="fieldTouchedStatus['notes'] && notesErrors.length !== 0 ? false : null" 
                        trim
                        @blur="setFieldTouched('notes')"
                    ></b-form-textarea>
                    <template #invalid-feedback>
                        <div v-for="err in notesErrors" :key="err" class="mt-1 text-danger font-size-70">{{ err }}</div>
                    </template>
            </b-form-group>

            <b-form-group
                v-if="isLocalFieldNameEnabled('instructions')"
                :id="`${inputPrefix}-group-instructions`"
                description="Instructions for suppliers, if suppliers can complete this Document Type themselves."
                label="Instructions for Suppliers"
                :label-for="`${inputPrefix}-input-instructions`"
                label-cols="4"
                label-class="font-weight-bold"
                required
                invalid-feedback="Please input instructions"
                :state="fieldTouchedStatus['instructions'] && instructionsErrors.length !== 0 ? false : null" 
                class="mt-4"
            >
                    <b-form-textarea 
                        :id="`${inputPrefix}-input-instructions`" 
                        v-model="formData.instructions" 
                        rows="8"
                        :state="fieldTouchedStatus['instructions'] && instructionsErrors.length !== 0 ? false : null" 
                        trim
                        @blur="setFieldTouched('instructions')"
                    ></b-form-textarea>
                    <template #invalid-feedback>
                        <div v-for="err in instructionsErrors" :key="err" class="mt-1 text-danger font-size-70">{{ err }}</div>
                    </template>
            </b-form-group>

            <div class="w-100 mt-5 d-flex gap-4 align-items-center justify-content-center">
                <b-button variant="gray-secondary" size="lg" class="px-5" @click="triggerCancel">Cancel</b-button>
                <b-button 
                    type="submit"
                    variant="primary" 
                    size="lg" 
                    :disabled="isLocked || saving || loadingMetadata || loading || !allValid || !formReady"
                    class="min-width-200 d-flex justify-content-center"
                >
                    <b-spinner v-if="saving" key="isSavingButtonContent" small class="mr-2"></b-spinner>
                    <span v-else key="notSavingButtonContent">Create</span>
                </b-button>
            </div>

        </b-form>

        <div v-else key="formNotReady" class="w-100 min-height-300 d-flex align-items-center justify-content-center">
            <div class="text-center">
                <b-spinner></b-spinner>
            </div>
        </div>

    </div>
</template>

<script>
import { errorHandling } from '@/js/custom-vue-mixins';
import { suppliosApi } from '@/js/api-service';
import { nanoid } from 'nanoid';
import cloneDeep from 'lodash/cloneDeep';
import CompSingleMultipleChip from '../shared/CompSingleMultipleChip.vue';

function getDefaultData() {
    return {
        title: '',
        instructions: '',
        notes: '',
        comp_item_type_id: '',
        kind_id: '',
        singleOrMultiple: null,  // multiple is more restrictive, so default to that?
        enable_valid_dates: true,  // not in form yet
        // internal_roles_subs_visible: '',  // LATER - it's an enum 
        // external_roles_subs_visible: '',  // LATER - it's an enum
    }
}

const localFieldNameToBackendFieldName = {
    title: 'title',  // but we call it 'Name' in the form
    instructions: 'instructions',
    notes: 'notes',
    comp_item_type_id: 'comp_item_type_id',
    kind_id: 'kind_id',
    singleOrMultiple: 'allow_vendor_scopes',
    enable_valid_dates: 'enable_valid_dates',  // not in form yet
}

export default {
    name: 'CompItemCreateForm',
    components: {
        CompSingleMultipleChip,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        existingData: {  // IF DOING UPDATE - handle this later!
            type: Object,
            required: false,
        },
        enabledFields: {  // use the field names that we use in backend, not the 'aliases' we may use here
            type: Array,
            default: () => ['title'],
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,  
            loadingMetadata: false,
            saving: false,
            localId: nanoid(),
            formData: {},
            fieldTouchedStatus: {},  // track if a field has been touched so we can show errors or not
            formReady: false,  // set true when you're done prepping initial data, if any
            uploadPending: false,  // are uploads happening?
            initialFormData: {},  // if you are updating existing, must put here (goes to value prop, not v-model)
            fileCategory: 'comp_sub',  // the s3file category in our backend 
            // defaults
            itemTypeOptions: [],
            kindOptions: [],  // must get from API { value, label, ... where value is id of CompItemKind}
        }
    },
    computed: {
        isLocked() {
            // do we want to lock form?  add logic later...
            return false;
        },
        isUpdate() {
            return !!this.existingData?.id;
        },
        inputPrefix() {
            return `comp-item-form-${this.isUpdate ? 'update' : 'create'}-${this.localId}`;
        },
        // VALIDATION -- this is annoying, but probably the best way to do it...
        titleErrors() {
            const errors = [];
            if (this.isLocalFieldNameEnabled('title') && this.formData.title?.length === 0) {
                errors.push('Please enter a Name for this Document Type');
            }
            return errors;
        },
        compItemTypeErrors() {
            const errors = [];
            if (this.isLocalFieldNameEnabled('comp_item_type_id') && !this.formData.comp_item_type_id) {
                errors.push('Please enter a Name for this Document Type');
            }
            return errors;
        },
        kindErrors() {
            const errors = [];
            if (this.isLocalFieldNameEnabled('kind_id') && !this.formData.kind_id) {
                errors.push('Please select a Kind of Document');
            }
            return errors;
        },
        singleOrMultipleErrors() {
            const errors = [];
            const validValues = ['single', 'multiple'];
            if (this.isLocalFieldNameEnabled('singleOrMultiple') && !validValues.includes(this.formData.singleOrMultiple)) {
                errors.push('Please select an option');
            }
            return errors;
        },
        notesErrors() {
            const errors = [];
            if (this.isLocalFieldNameEnabled('notes') && this.formData.notes?.length === 0) {
                errors.push('Please input notes');
            }
            return errors;
        },
        instructionsErrors() {
            const errors = [];
            if (this.isLocalFieldNameEnabled('instructions') && this.formData.instructions?.length === 0) {
                errors.push('Please input instructions');
            }
            return errors;
        },
        enableValidDatesErrors() {
            const errors = [];
            if (this.isLocalFieldNameEnabled('enable_valid_dates') && this.formData.enable_valid_dates === undefined) {
                errors.push('Please select an option for expiration tracking');
            }
            return errors;
        },
        allValid() {
            // check all fields for validity
            const errorLists = [this.titleErrors, this.compItemTypeErrors, this.singleOrMultipleErrors, this.notesErrors, this.instructionsErrors, this.enableValidDatesErrors, this.kindErrors];
            return errorLists.every(e => e.length === 0);
        },
    },
    created() {
        this.fieldTouchedStatus = Object.fromEntries(Object.keys(localFieldNameToBackendFieldName).map(f => [f, false]));
    },
    async mounted() {
        // do something depending on being a create/update?
        await this.getCreateMeta();
        if (this.isUpdate) {
            this.formData = cloneDeep(this.existingData);
            this.formReady = true;
        } else {
            this.formData = getDefaultData();
            this.formReady = true;
        }
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getCreateMeta() {
            this.loadingMetadata = true;
            let resp;
            try {
                resp = await suppliosApi.compItemStd.get_create_meta();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp.data) {
                // TODO  stash the stuff you need for the form
                const rawItemTypes = Array.isArray(resp.data.item_types) ? resp.data.item_types : [];
                const emptyItemTypeOption = { value: '', label: '-- Select Category --' };
                const itemTypes = rawItemTypes.map(it => ({ value: it.id, label: it.name }));  // should already be sorted in backend
                this.itemTypeOptions = [emptyItemTypeOption, ...itemTypes];
                // KINDS
                const rawKindOptions = Array.isArray(resp.data.kinds) ? resp.data.kinds : [];
                const remappedKinds = rawKindOptions.map(k => ({ value: k.id, label: k.label }));  // should already be sorted in backend
                const emptyKindOption = { value: '', label: '-- Select Kind --' };
                this.kindOptions = [emptyKindOption, ...remappedKinds];
                // NEEDED? this.reminderActionOptions = [...reminderActionOptions];
                // NEEDED? this.reminderPeriodOptions = [...reminderPeriodOptions];
            }
            this.loadingMetadata = false;
        },
        isLocalFieldNameEnabled(localFieldName) {
            const backendFieldName = localFieldNameToBackendFieldName[localFieldName];
            return this.enabledFields.includes(backendFieldName);
        },
        getFieldState(fieldName) {
            console.log('getFieldState for:', fieldName);
            return null;  // TODO
        },
        setFieldTouched(localFieldName) {
            this.$set(this.fieldTouchedStatus, localFieldName, true);
        },
        triggerCancel() {
            this.$emit('cancel');  // also need to reset things?  or let it get destroyed?
        },
        formatDataForSave(formData) {
            // do anything with the data before sending to API
            // must get the file IDs out and send those in array of 'files'
            console.log('data we need to format for save:', formData);
            // handle special things we can't pass directly to API
            const saveData = {};
            for (const [localFieldName, backendFieldName] of Object.entries(localFieldNameToBackendFieldName)) {
                if (this.isLocalFieldNameEnabled(localFieldName)) {
                    const rawValue = formData[localFieldName];
                    // have to handle special cases
                    if (localFieldName === 'singleOrMultiple') {
                        saveData['allow_vendor_scopes'] = rawValue === 'multiple';  // 'multiple' becomes allow_vendor_scopes=true
                    } else {
                        saveData[backendFieldName] = rawValue;
                    }
                }
            }
            return saveData;
        },
        setSavingStatus(isSaving) {
            this.saving = isSaving;
            this.$emit('loading-status', isSaving);
        },
        async handleSubmit(payload) {
            console.log('*** From handleSubmit, here is payload, then formData we start with:');
            console.log('payload:', payload);
            console.log('this.formData', this.formData);
            let dataToSubmit = this.formatDataForSave(this.formData);
            console.log('*** Here is data we are going to submit to API:', dataToSubmit);
            this.setSavingStatus(true);
            let returnData;
            try {
                if (this.isUpdate) {
                    console.error('UPDATES IN THIS FORM ARE NOT IMPLEMENTED YET');
                } else { 
                    const resp = await suppliosApi.compItemStd.create_comp_item(dataToSubmit);
                    returnData = resp.data;
                    console.log('got response from creating new CompItem', returnData);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (returnData) {
                if (this.isUpdate) {
                    this.$emit('updated', returnData);
                } else {
                    this.$emit('created', returnData);
                }
                this.$emit('saved', returnData);  // common for both update/create
            }
            this.setSavingStatus(false);
            return returnData;  // make sure not to return until after you set saving status back to false
        },
    }
}
</script>

<style>

</style>